import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  Briefcase, 
  Search, 
  LogOut, 
  ChevronRight, 
  Plus, 
  CheckCircle, 
  XCircle, 
  User as UserIcon,
  ShieldCheck,
  Zap,
  Clock,
  ExternalLink,
  Mail,
  MoreVertical,
  ChevronDown,
  Building2,
  Lock,
  FileText,
  DollarSign,
  MapPin,
  ClipboardList,
  Paperclip,
  Activity,
  ArrowLeft,
  Phone,
  Globe,
  Link as LinkIcon
} from 'lucide-react';
import axiosInstance from '../../utils/axiosInstance';
import logo from '../dashboard/icon2.png';

const AdminPanel = () => {
  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); 
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Data State
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  
  // Selection State
  const [selectedAgent, setSelectedAgent] = useState(null); 
  const [showJobModal, setShowJobModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [detailAgent, setDetailAgent] = useState(null);
  const [agentHistory, setAgentHistory] = useState([]);

  // Job Form State
  const [jobForm, setJobForm] = useState({
    job_title: "",
    company: "",
    location: "",
    status: "applied",
    job_url: "",
    manual_apply_url: ""
  });

  const getHeaders = useCallback(() => ({
    'X-Admin-Password': password
  }), [password]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axiosInstance.post('/admin/login', { password });
      setIsAuthenticated(true);
      localStorage.setItem("admin_pwd", password);
      setError("");
    } catch (err) {
      setError("Unauthorized access");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (q = "") => {
    try {
      const res = await axiosInstance.get(`/admin/users?q=${q}`, { headers: getHeaders() });
      setUsers(res.data);
    } catch (err) { console.error(err); }
  }, [getHeaders]);

   const fetchAgents = useCallback(async (q = "") => {
    try {
       const res = await axiosInstance.get(`/admin/agents/all?q=${q}`, { headers: getHeaders() });
       setAgents(res.data);
    } catch (err) { console.error(err); }
  }, [getHeaders]);

  const fetchAgentHistory = useCallback(async (agentId) => {
    try {
      const res = await axiosInstance.get(`/admin/agents/${agentId}/history`, { headers: getHeaders() });
      setAgentHistory(res.data);
    } catch (err) { console.error(err); }
  }, [getHeaders]);

  useEffect(() => {
    const saved = localStorage.getItem("admin_pwd");
    if (saved) {
      setPassword(saved);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === 'users') fetchUsers();
      if (activeTab === 'agents') fetchAgents();
    }
  }, [isAuthenticated, activeTab, fetchUsers, fetchAgents]);

  const handleUpdateSubscription = async (userId, newTier) => {
    try {
      await axiosInstance.patch(`/admin/users/${userId}/subscription`, { tier: newTier }, { headers: getHeaders() });
      setUsers(users.map(u => u.id === userId ? { ...u, tier: newTier } : u));
      setSuccessMsg(`User updated to ${newTier.toUpperCase()}`);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setError("Update failed");
    }
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    if (!selectedAgent) return;
    setLoading(true);
    try {
      await axiosInstance.post('/admin/agent-history', {
        user_id: selectedAgent.user_id,
        agent_id: selectedAgent.id,
        ...jobForm
      }, { headers: getHeaders() });
      
      setSuccessMsg("Job result recorded!");
      setShowJobModal(false);
      setJobForm({ job_title: "", company: "", location: "", status: "applied", job_url: "", manual_apply_url: "" });
      setTimeout(() => setSuccessMsg(""), 3000);
      fetchAgents(); // Refresh counts
      if (detailAgent) fetchAgentHistory(detailAgent.id); 
    } catch (err) {
      setError("Failed to record job");
    } finally {
      setLoading(false);
    }
  };

  const toggleDescription = (id) => {
    setExpandedDescriptions(prev => ({...prev, [id]: !prev[id]}));
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_pwd");
    setIsAuthenticated(false);
    setPassword("");
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-md p-8 bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100">
          <div className="flex flex-col items-center mb-10">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-xl shadow-blue-500/20 mb-6 group transition-all duration-300">
                <ShieldCheck className="text-white group-hover:scale-110 transition-transform" size={32}/>
            </div>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Admin Portal</h1>
            <p className="text-slate-500 mt-2 font-medium">Verify your credentials to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
                <label className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Access Key</label>
                <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" size={18}/>
                    <input 
                      type="password" 
                      className="w-full pl-12 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold placeholder:font-medium"
                      placeholder="••••••••"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoFocus
                    />
                </div>
            </div>
            {error && <div className="p-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold rounded-2xl flex items-center gap-2"><XCircle size={16}/>{error}</div>}
            <button disabled={loading} type="submit" className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-xl shadow-slate-900/10 active:scale-[0.98]">
              {loading ? "Authenticating..." : "Authorize Access"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50/50 text-slate-900 selection:bg-blue-100 selection:text-blue-900">
      
      {/* Sidebar - Matching DashboardSidebar Style */}
      <aside className="w-64 h-full shrink-0 flex flex-col bg-white border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.03)] fixed left-0 z-30">
        <div className="flex items-center gap-3 px-6 h-16 shrink-0 border-b border-slate-100">
          <img src={logo} alt="AI Job Mate" className="w-8 h-8 object-contain" />
          <div>
            <div className="font-bold text-slate-900 leading-tight tracking-tight uppercase italic underline decoration-blue-600/30">Admin Central</div>
            <div className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">Control Panel</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-6 space-y-1">
            <button 
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group relative ${activeTab === 'users' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
            >
                {activeTab === 'users' && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-md" />}
                <div className="flex items-center gap-3 ml-2">
                  <Users className={`w-5 h-5 transition-colors ${activeTab === 'users' ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}`} strokeWidth={2}/>
                  <span className={`${activeTab === 'users' ? 'font-semibold' : 'font-medium'}`}>User Directory</span>
                </div>
            </button>
            <button 
                onClick={() => setActiveTab('agents')}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 group relative ${activeTab === 'agents' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`}
            >
                {activeTab === 'agents' && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-blue-600 rounded-r-md" />}
                <div className="flex items-center gap-3 ml-2">
                  <Briefcase className={`w-5 h-5 transition-colors ${activeTab === 'agents' ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-500'}`} strokeWidth={2}/>
                  <span className={`${activeTab === 'agents' ? 'font-semibold' : 'font-medium'}`}>Global Agents</span>
                </div>
            </button>
        </nav>

        <div className="p-4 border-t border-slate-100 bg-slate-50/50">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all text-sm group">
                <LogOut size={18} className="group-hover:-translate-x-1 transition-transform"/>
                <span>Terminal Exit</span>
            </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 ml-64 p-10 min-w-0">
        
        {detailAgent ? (
          <div className="space-y-10 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Detail View Header */}
            <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-200/60">
                <div className="flex items-center gap-6">
                    <button 
                        onClick={() => setDetailAgent(null)}
                        className="p-4 bg-white border border-slate-200 text-slate-400 rounded-2xl hover:text-blue-600 hover:border-blue-500 hover:shadow-lg transition-all"
                    >
                        <ArrowLeft size={24}/>
                    </button>
                    <div>
                        <h1 className="text-4xl font-black text-slate-900 tracking-tight italic">
                            {detailAgent.title}
                        </h1>
                        <p className="text-slate-400 font-bold text-sm mt-1 flex items-center gap-2 uppercase tracking-widest">
                           {detailAgent.user_email} <ChevronRight size={14}/> Operational History
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                     <div className="text-right">
                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Impact</div>
                        <div className="text-2xl font-black text-blue-600">{detailAgent.applications_count} Records</div>
                     </div>
                     <button 
                        onClick={() => {
                            setSelectedAgent(detailAgent);
                            setShowJobModal(true);
                        }}
                        className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-black hover:-translate-y-1 transition-all shadow-xl shadow-slate-900/10"
                    >
                        <Plus size={18}/> Link New Achievement
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-10 items-start">
                {/* History Table */}
                <div className="xl:col-span-7">
                    <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-[0_15px_40px_rgba(0,0,0,0.02)] overflow-hidden">
                        <div className="px-8 py-6 border-b border-slate-50 flex items-center justify-between bg-slate-50/30">
                            <h3 className="font-black text-slate-900 uppercase tracking-widest text-xs flex items-center gap-3">
                                <ClipboardList size={18} className="text-blue-600"/> achievement Ledger
                            </h3>
                            <button onClick={() => fetchAgentHistory(detailAgent.id)} className="text-[10px] font-bold text-blue-600 hover:underline">
                                Refresh Transaction Log
                            </button>
                        </div>
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="bg-white border-b border-slate-50">
                                    <th className="px-8 py-5 text-left text-[9px] font-black uppercase text-slate-400 tracking-widest">Opportunity</th>
                                    <th className="px-8 py-5 text-left text-[9px] font-black uppercase text-slate-400 tracking-widest">Status</th>
                                    <th className="px-8 py-5 text-left text-[9px] font-black uppercase text-slate-400 tracking-widest">Timestamp</th>
                                    <th className="px-8 py-5 text-right text-[9px] font-black uppercase text-slate-400 tracking-widest">Source</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {agentHistory.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20 text-center">
                                            <div className="flex flex-col items-center gap-4 text-slate-300">
                                                <Activity size={48} className="opacity-20"/>
                                                <p className="text-xs font-bold uppercase tracking-widest">No achievements found</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : agentHistory.map(entry => (
                                    <tr key={entry.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-8 py-5">
                                            <div className="font-bold text-slate-900 leading-tight">{entry.job_title}</div>
                                            <div className="text-[10px] font-black text-blue-600 uppercase tracking-tight mt-0.5">{entry.company}</div>
                                            <div className="text-[9px] text-slate-400 font-medium italic mt-1">{entry.location || "Global"}</div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-wider ${
                                                entry.status === 'applied' ? 'bg-emerald-50 text-emerald-600' : 
                                                entry.status === 'manual_apply' ? 'bg-blue-50 text-blue-600' : 
                                                'bg-slate-100 text-slate-500'
                                            }`}>
                                                {entry.status}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-[10px] font-black text-slate-400 tabular-nums">
                                            {new Date(entry.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-8 py-5 text-right whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-2">
                                                {entry.job_url && (
                                                    <a href={entry.job_url} target="_blank" rel="noreferrer" className="p-2 bg-slate-50 text-slate-400 rounded-xl hover:bg-slate-900 hover:text-white transition-all shadow-sm">
                                                        <ExternalLink size={14}/>
                                                    </a>
                                                )}
                                                {entry.manual_apply_url && (
                                                    <a href={entry.manual_apply_url} target="_blank" rel="noreferrer" className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                                                        <Zap size={14}/>
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Add Achievement Sidebar Panel */}
                <div className="xl:col-span-5">
                    <div className="bg-white rounded-[2.5rem] border border-slate-200/60 p-10 shadow-sm sticky top-10 space-y-8">
                        <div>
                            <h3 className="text-2xl font-black text-slate-900 tracking-tight italic flex items-center gap-3">
                                <Zap className="text-blue-600" size={24}/> Inject Outcome
                            </h3>
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Manual achievement Entry</p>
                        </div>

                        <form onSubmit={(e) => {
                            setSelectedAgent(detailAgent);
                            handleSubmitJob(e);
                        }} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Designation</label>
                                <div className="relative group">
                                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-600 transition-colors" size={18}/>
                                    <input 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        placeholder="Software Engineer"
                                        required
                                        value={jobForm.job_title}
                                        onChange={e => setJobForm({...jobForm, job_title: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Company Entity</label>
                                <div className="relative group">
                                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-blue-600 transition-colors" size={18}/>
                                    <input 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold"
                                        placeholder="OpenAI"
                                        required
                                        value={jobForm.company}
                                        onChange={e => setJobForm({...jobForm, company: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Outcome</label>
                                    <select 
                                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all appearance-none"
                                        value={jobForm.status}
                                        onChange={e => setJobForm({...jobForm, status: e.target.value})}
                                    >
                                        <option value="applied">Applied</option>
                                        <option value="manual_apply">Call to Action</option>
                                        <option value="pending">Pending</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Locality</label>
                                    <input 
                                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        placeholder="Remote"
                                        value={jobForm.location}
                                        onChange={e => setJobForm({...jobForm, location: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Link</label>
                                <input 
                                    className="w-full px-4 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                    placeholder="https://openai.com/jobs/..."
                                    value={jobForm.job_url}
                                    onChange={e => setJobForm({...jobForm, job_url: e.target.value})}
                                />
                            </div>

                            <button 
                                type="submit"
                                disabled={loading}
                                className="w-full py-5 bg-slate-900 text-white rounded-[1.5rem] font-black uppercase tracking-widest text-xs shadow-2xl shadow-slate-900/10 hover:bg-black hover:-translate-y-1 transition-all active:scale-95"
                            >
                                {loading ? "Syncing..." : "Confirm & Commit result"}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
          </div>
        ) : (
          <>
        <header className="flex items-center justify-between mb-10 pb-6 border-b border-slate-200/60">
            <div>
                <h1 className="text-4xl font-black text-slate-900 tracking-tight italic">
                    {activeTab === 'users' ? "Users" : "Agents"}
                </h1>
                <p className="text-slate-400 font-bold text-sm mt-1 flex items-center gap-2 uppercase tracking-widest">
                   System Management <ChevronRight size={14}/> {activeTab}
                </p>
            </div>
            
            <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18}/>
                <input 
                    className="w-96 pl-12 pr-6 py-3.5 bg-white border border-slate-200 rounded-[1.25rem] text-sm font-bold shadow-sm outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all placeholder:font-medium placeholder:text-slate-300"
                    placeholder={activeTab === 'users' ? "Search Name or Email..." : "Search Agent, Email or Keyword..."}
                    value={searchQuery}
                    onChange={e => {
                        setSearchQuery(e.target.value);
                        if (activeTab === 'users') fetchUsers(e.target.value);
                        else fetchAgents(e.target.value);
                    }}
                />
            </div>
        </header>

        {successMsg && (
            <div className="mb-8 p-4 bg-emerald-500 text-white rounded-[1.25rem] flex items-center gap-3 font-bold text-sm shadow-xl shadow-emerald-500/20 animate-in fade-in slide-in-from-top-4 duration-500">
                <CheckCircle size={20}/> {successMsg}
            </div>
        )}

        {/* Users Tab Content */}
        {activeTab === 'users' && (
            <div className="bg-white rounded-[2rem] border border-slate-200/60 shadow-[0_10px_40px_rgba(0,0,0,0.02)] overflow-hidden">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase text-slate-400 tracking-widest">User Profile</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase text-slate-400 tracking-widest">Professional Intel</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase text-slate-400 tracking-widest">Subscription</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase text-slate-400 tracking-widest">Activity</th>
                            <th className="px-8 py-6 text-right text-[10px] font-black uppercase text-slate-400 tracking-widest">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {users.map(u => (
                            <tr key={u.id} className="hover:bg-slate-50/30 transition-colors group">
                                <td className="px-8 py-7">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-slate-100 rounded-2xl flex items-center justify-center text-slate-400 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
                                            <UserIcon size={24}/>
                                        </div>
                                        <div>
                                            <div className="font-black text-slate-900 text-lg tracking-tight leading-none mb-1">{u.first_name} {u.last_name}</div>
                                            <div className="text-[10px] text-slate-400 font-bold flex items-center gap-1.5 underline decoration-slate-200 uppercase tracking-widest">
                                                <Mail size={10}/> {u.email}
                                            </div>
                                            <div className={`text-[10px] font-black flex items-center gap-1.5 mt-1 ${u.phone ? 'text-emerald-600' : 'text-slate-300'}`}>
                                                <Phone size={10} className={u.phone ? "fill-emerald-50" : ""}/> 
                                                {u.phone || "PH: NOT PROVIDED"}
                                            </div>
                                            {(u.role || u.location) && (
                                                <div className="flex items-center gap-3 mt-2">
                                                    {u.role && <span className="text-[9px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded uppercase">{u.role}</span>}
                                                    {u.location && <span className="text-[9px] font-black text-slate-400 flex items-center gap-1 uppercase"><MapPin size={8}/> {u.location}</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-8 py-7">
                                    <div className="max-w-xs">
                                        {u.skills && u.skills.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 mb-2">
                                                {u.skills.slice(0, 3).map(s => (
                                                    <span key={s} className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-[8px] font-black uppercase">{s}</span>
                                                ))}
                                                {u.skills.length > 3 && <span className="text-[8px] font-black text-slate-300">+{u.skills.length - 3} MORE</span>}
                                            </div>
                                        ) : (
                                            <div className="text-[9px] font-bold text-slate-300 uppercase tracking-widest mb-1">No Skills Loaded</div>
                                        )}
                                        {u.bio && (
                                            <p className="text-[10px] text-slate-500 font-medium italic line-clamp-2 leading-relaxed">
                                                "{u.bio}"
                                            </p>
                                        )}
                                        {u.urls && u.urls.length > 0 && (
                                            <div className="mt-3 flex items-center gap-2">
                                                <span className="text-[9px] font-black text-slate-400 flex items-center gap-1 uppercase">
                                                    <Globe size={10}/> {u.urls.length} Digital Links
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-8 py-7">
                                    <div className="space-y-3">
                                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                                            u.tier === 'pro' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 
                                            u.tier === 'elite' ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' : 
                                            'bg-slate-200 text-slate-600'
                                        }`}>
                                            {u.tier === 'pro' && <Zap size={10} fill="currentColor"/>}
                                            {u.tier === 'elite' && <ShieldCheck size={10}/>}
                                            {u.tier === 'elite' ? 'Enterprise' : u.tier} Plan
                                        </div>
                                        <div className="relative w-40">
                                            <select 
                                                value={u.tier}
                                                onChange={(e) => handleUpdateSubscription(u.id, e.target.value)}
                                                className="w-full text-[10px] font-black text-slate-500 bg-white border border-slate-200 rounded-xl px-3 py-2 outline-none cursor-pointer hover:border-blue-500 focus:ring-4 focus:ring-blue-500/10 transition-all appearance-none uppercase"
                                            >
                                                <option value="starter">Starter</option>
                                                <option value="pro">Pro</option>
                                                <option value="elite">Enterprise</option>
                                            </select>
                                            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={12}/>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-8 py-7">
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-slate-900 font-black text-xs">
                                            <Briefcase size={14} className="text-blue-500"/>
                                            {u.agent_count || 0} Agents
                                        </div>
                                        <div className="flex items-center gap-2 text-slate-900 font-black text-xs">
                                            <FileText size={14} className="text-purple-500"/>
                                            {u.resume_count || 0} Assets (CVs)
                                        </div>
                                        {u.trial_ends_at && (
                                            <div className="flex items-center gap-2 text-rose-500 font-black text-[9px] uppercase tracking-tighter">
                                                <Clock size={12}/> Trial Ends: {new Date(u.trial_ends_at).toLocaleDateString()}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-widest">
                                            <Clock size={12} className="text-slate-300"/>
                                            Joined {new Date(u.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-8 py-7 text-right">
                                    <button className="p-3 bg-slate-50 text-slate-400 rounded-2xl hover:bg-slate-900 hover:text-white transition-all shadow-sm">
                                        <MoreVertical size={20}/>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

        {/* Agents Tab Content - Enhanced with Details */}
        {activeTab === 'agents' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {agents.map(agent => (
                    <div 
                        key={agent.id} 
                        onClick={() => {
                            if (activeTab === 'agents') {
                                setDetailAgent(agent);
                                fetchAgentHistory(agent.id);
                            }
                        }}
                        className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-[0_15px_40px_rgba(0,0,0,0.02)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.04)] transition-all flex flex-col group p-8 relative overflow-hidden cursor-pointer"
                    >
                        
                        {/* Header Section */}
                        <div className="flex justify-between items-start mb-8 pb-6 border-b border-slate-50">
                            <div className="flex items-center gap-4">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${agent.status === 'running' ? 'bg-emerald-500 shadow-xl shadow-emerald-500/20' : 'bg-slate-100 text-slate-400'}`}>
                                    <Zap className={agent.status === 'running' ? 'text-white' : 'text-slate-300'} size={28}/>
                                </div>
                                <div className="space-y-1">
                                    <h3 className="text-2xl font-black text-slate-900 tracking-tight leading-tight group-hover:text-blue-600 transition-colors">{agent.title}</h3>
                                    <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                                        <Mail size={12} className="text-blue-500/50"/> {agent.user_email}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right flex flex-col items-end">
                                <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider mb-2 ${
                                    agent.status === 'running' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
                                }`}>
                                    {agent.status}
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Records</span>
                                    <span className="text-xl font-black text-blue-600">{agent.applications_count}</span>
                                </div>
                            </div>
                        </div>

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-6 mb-8">
                            <div className="space-y-4">
                                <div className="flex items-start gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 group-hover:bg-white transition-colors">
                                    <div className="p-2 bg-blue-50 rounded-xl text-blue-600"><MapPin size={18}/></div>
                                    <div>
                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Target Location</div>
                                        <div className="text-sm font-bold text-slate-700">{agent.location || "Remote / Global"}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 group-hover:bg-white transition-colors">
                                    <div className="p-2 bg-purple-50 rounded-xl text-purple-600"><FileText size={18}/></div>
                                    <div>
                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Attached Resume</div>
                                        <div className="text-sm font-bold text-slate-700">{agent.resume_title || "Standard Profile"}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-start gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 group-hover:bg-white transition-colors">
                                    <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600"><DollarSign size={18}/></div>
                                    <div>
                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Salary Specs</div>
                                        <div className="text-sm font-bold text-slate-700">{agent.expected_salary ? `Exp: ${agent.expected_salary}` : agent.salary_range || "Open Negotiable"}</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100 group-hover:bg-white transition-colors">
                                    <div className="p-2 bg-pink-50 rounded-xl text-pink-600"><Activity size={18}/></div>
                                    <div>
                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Operational Mode</div>
                                        <div className="text-sm font-bold text-slate-700">{agent.auto_apply ? "Autonomous" : "Manual Assisted"}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Extra Requirements & Files */}
                        <div className="space-y-6 flex-1">
                            {/* Location Preference & Daily Limit */}
                            <div className="flex items-center gap-4">
                                <div className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                    <Zap size={10} fill="currentColor"/> {agent.jobs_per_day} Apps Day
                                </div>
                                {agent.location_preference && (
                                    <div className="px-4 py-2 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest border border-blue-100">
                                        Pref: {agent.location_preference}
                                    </div>
                                )}
                            </div>

                            {/* Job Description Expandable */}
                            {agent.job_description && (
                                <div className="p-6 bg-slate-50 rounded-[1.5rem] border border-slate-100 relative group/desc">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                                            <ClipboardList size={14}/> Context / Description
                                        </div>
                                        <button 
                                            onClick={() => toggleDescription(agent.id)}
                                            className="text-[10px] font-bold text-blue-600 hover:underline"
                                        >
                                            {expandedDescriptions[agent.id] ? "Collapse" : "Read Full Context"}
                                        </button>
                                    </div>
                                    <div className={`text-xs font-medium text-slate-500 leading-relaxed overflow-hidden transition-all duration-300 ${expandedDescriptions[agent.id] ? 'max-h-[500px]' : 'max-h-12'}`}>
                                        {agent.job_description}
                                    </div>
                                    {!expandedDescriptions[agent.id] && (
                                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none rounded-b-[1.5rem]"></div>
                                    )}
                                </div>
                            )}

                            {/* Attached Files Section */}
                            {agent.attached_files && agent.attached_files.length > 0 && (
                                <div className="space-y-3">
                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Paperclip size={14}/> Attached Documents ({agent.attached_files.length})
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {agent.attached_files.map((file, idx) => (
                                            <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-xl hover:border-blue-500 hover:shadow-sm transition-all cursor-default">
                                                <div className="p-1 bg-slate-100 rounded text-slate-400"><FileText size={12}/></div>
                                                <span className="text-[10px] font-bold text-slate-600 truncate max-w-[120px]">{file.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-10 pt-8 border-t border-slate-50 flex items-center gap-4">
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedAgent(agent);
                                    setShowJobModal(true);
                                }}
                                className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-black hover:-translate-y-1 transition-all shadow-xl shadow-slate-900/10"
                            >
                                <Plus size={18}/> Link New Achievement
                            </button>
                            <button 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setDetailAgent(agent);
                                    fetchAgentHistory(agent.id);
                                }}
                                className="p-4 bg-white border border-slate-200 text-slate-400 rounded-2xl hover:text-blue-600 hover:border-blue-500 hover:shadow-lg transition-all active:scale-[0.9]"
                            >
                                <ExternalLink size={20}/>
                            </button>
                        </div>

                    </div>
                ))}
            </div>
        )}
        </>
        )}

        {/* Improved Universal Modal - Redesigned */}
        {showJobModal && selectedAgent && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
                <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity duration-300" onClick={() => setShowJobModal(false)}></div>
                <div className="relative bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-white animate-in zoom-in-95 duration-200">
                    
                    {/* Modal Header */}
                    <div className="bg-slate-50 p-12 relative flex items-center justify-between border-b border-slate-100">
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-blue-600 rounded-xl shadow-lg shadow-blue-500/20"><Zap className="text-white" size={20}/></div>
                                <h3 className="text-4xl font-black tracking-tight text-slate-900 italic">Inject Result</h3>
                            </div>
                            <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest flex items-center gap-2 px-1">
                                Agent System <ChevronRight size={10}/> <span className="text-blue-600">"{selectedAgent.title}"</span>
                            </p>
                        </div>
                        <button onClick={() => setShowJobModal(false)} className="p-4 bg-white border border-slate-200 rounded-2xl text-slate-400 hover:text-red-500 hover:border-red-100 hover:shadow-lg transition-all active:rotate-90">
                            <XCircle size={24}/>
                        </button>
                    </div>
                    
                    {/* Form Scroll Area */}
                    <form onSubmit={handleSubmitJob} className="p-12 space-y-8 bg-white max-h-[60vh] overflow-y-auto custom-scrollbar">
                        
                        <div className="grid grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Job Designation</label>
                                <div className="relative group">
                                    <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors group-focus-within:text-blue-600" size={18}/>
                                    <input 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-[15px] font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all shadow-sm"
                                        value={jobForm.job_title}
                                        onChange={e => setJobForm({...jobForm, job_title: e.target.value})}
                                        required
                                        placeholder="Lead Product Designer"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Organization Name</label>
                                <div className="relative group">
                                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors group-focus-within:text-blue-600" size={18}/>
                                    <input 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-[15px] font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all shadow-sm"
                                        value={jobForm.company}
                                        onChange={e => setJobForm({...jobForm, company: e.target.value})}
                                        required
                                        placeholder="Meta (Facebook)"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-8">
                             <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Office Location</label>
                                <div className="relative group">
                                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors group-focus-within:text-blue-600" size={18}/>
                                    <input 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-[15px] font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all shadow-sm"
                                        value={jobForm.location}
                                        onChange={e => setJobForm({...jobForm, location: e.target.value})}
                                        placeholder="Palo Alto, CA (Hybrid)"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Application Outcome</label>
                                <div className="relative group">
                                    <Activity className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors group-focus-within:text-blue-600" size={18}/>
                                    <select 
                                        className="w-full pl-12 pr-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-[15px] font-black outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all shadow-sm appearance-none"
                                        value={jobForm.status}
                                        onChange={e => setJobForm({...jobForm, status: e.target.value})}
                                    >
                                        <option value="applied">Application Initiated</option>
                                        <option value="manual_apply">Call to Action (Success)</option>
                                        <option value="pending">Review Underway</option>
                                    </select>
                                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={16}/>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Verification URL / Job Source</label>
                            <div className="relative group">
                                <ExternalLink className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors group-focus-within:text-blue-600" size={18}/>
                                <input 
                                    className="w-full pl-12 pr-6 py-4 bg-slate-50/50 border border-slate-200 rounded-2xl text-[15px] font-bold outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all shadow-sm"
                                    value={jobForm.job_url}
                                    onChange={e => setJobForm({...jobForm, job_url: e.target.value})}
                                    placeholder="https://metacareers.com/..."
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-blue-50 rounded-[1.5rem] border border-blue-100 flex items-center gap-4">
                            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/20"><Building2 size={18}/></div>
                            <div>
                                <div className="text-[10px] font-black text-blue-400 uppercase tracking-widest">Ownership</div>
                                <div className="text-sm font-black text-blue-900">{selectedAgent.user_email}</div>
                            </div>
                        </div>

                    </form>

                    {/* Modal Footer */}
                    <div className="p-12 bg-slate-50 border-t border-slate-100">
                        <button 
                            onClick={handleSubmitJob}
                            disabled={loading}
                            className="w-full py-5 bg-slate-900 hover:bg-black text-white rounded-[1.5rem] font-black uppercase tracking-[0.25em] text-sm shadow-2xl shadow-slate-900/10 active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                        >
                            {loading ? "Registering Transaction..." : "Confirm & Link Result"}
                        </button>
                    </div>
                </div>
            </div>
        )}

      </main>

      {/* Global Aesthetics Overlay */}
      <div className="fixed bottom-12 right-12 z-[40]">
          <div className="bg-white/80 backdrop-blur-xl border border-slate-200 p-4 rounded-[1.5rem] shadow-2xl shadow-slate-200/50 flex items-center gap-4 border-l-4 border-l-blue-600">
              <div className="w-2.5 h-2.5 bg-blue-600 rounded-full animate-ping"></div>
              <span className="text-xs font-black text-slate-900 uppercase tracking-widest italic">Core Admin Engine</span>
          </div>
      </div>

    </div>
  );
};

export default AdminPanel;
